(() => {
  "use strict";

  const API_ROOT =
    "https://apicloud.mythinkcar.cn/api/api/vehicleModelData";
  const DEFAULT_PRODUCT_NAME = "THINKSCAN 689BT";
  const DEFAULT_PRODUCT_LABEL = "THINKSCAN 689BT";
  // Coverage API returns an empty data set when size is greater than 100.
  const PAGE_SIZE = 100;
  // Fetch every page, but limit parallel requests to protect the API.
  const PAGE_FETCH_BATCH_SIZE = 4;
  const PAGE_RETRY_COUNT = 2;
  const GROUP_BATCH_SIZE = 8;

  function svgIcon(name) {
    const icons = {
      plus:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
      check:
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4.2 4L19 6.8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    };
    return icons[name] || "";
  }

  function cleanSystemLabel(value) {
    const raw = String(value || "Allgemein")
      .replace(/\s+/g, " ")
      .trim();
    const withoutDatabaseCode = raw.replace(
      /^(?:0x)?(?:\d{2}|[A-F]\d|\d[A-F])(?=\s|[-–—:.)]|$)\s*(?:[-–—:.)]\s*)?/i,
      ""
    );
    const cleaned = withoutDatabaseCode.replace(/\s*[:：]\s*$/, "").trim();
    return cleaned || raw || "Allgemein";
  }

  function coverageRecordDetail(row, action) {
    return [
      row.subSystem && `Untersystem: ${row.subSystem}`,
      row.subFunction && row.subFunction !== action ? row.subFunction : "",
      row.engine && `Motor: ${row.engine}`,
      row.trimLevel && `Ausstattung: ${row.trimLevel}`,
      row.bodyType && `Karosserie: ${row.bodyType}`,
      row.succession && `Baureihe: ${row.succession}`,
      row.chassis && `Fahrgestell: ${row.chassis}`,
      row.department && `Bauteil: ${row.department}`,
      row.region && `Region: ${row.region}`,
      row.connector && `Anschluss: ${row.connector}`,
      row.version && `Version: ${row.version}`,
      row.make && `Hinweis: ${row.make}`
    ]
      .filter(Boolean)
      .join(" · ");
  }

  function completeCoverageRecordKey(row) {
    return [
      row.modelName,
      row.model,
      row.modelYear,
      row.make,
      row.system,
      row.subSystem,
      row.function,
      row.subFunction,
      row.engine,
      row.trimLevel,
      row.bodyType,
      row.succession,
      row.chassis,
      row.department,
      row.region,
      row.connector,
      row.version
    ]
      .map((value) => String(value || "").trim())
      .map((value) => JSON.stringify(value)).join("::");
  }

  function uniqueSorted(values) {
    return [...new Set(values.filter(Boolean))].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base"
      })
    );
  }

  function fulfilledOrThrow(results) {
    const failed = results.find((result) => result.status === "rejected");
    if (failed) throw failed.reason;
    return results.map((result) => result.value);
  }

  function yearSort(a, b) {
    const ay = Number(String(a).match(/\d{4}/)?.[0] || 0);
    const by = Number(String(b).match(/\d{4}/)?.[0] || 0);
    return by - ay || String(b).localeCompare(String(a));
  }

  function setOptions(select, items, placeholder, valueKey = null, labelKey = null) {
    select.replaceChildren();
    const prompt = document.createElement("option");
    prompt.value = "";
    prompt.textContent = placeholder;
    select.append(prompt);

    items.forEach((item) => {
      const option = document.createElement("option");
      option.value = valueKey ? item[valueKey] : item;
      option.textContent = labelKey ? item[labelKey] : item;
      select.append(option);
    });
  }

  function buildUrl(path, params) {
    const url = new URL(`${API_ROOT}/${path}`);
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    });
    if (!url.searchParams.has("lang")) url.searchParams.set("lang", "DE");
    if (!url.searchParams.has("lan")) url.searchParams.set("lan", "DE");
    // User approved canonical model/year keys on 2026-10-08.
    // Original directory keys stay EN. Details prefer DE, with explicit EN failover.
    const canonicalDirectory = path === "getModelSelect" && [1, 2].includes(Number(params?.type));
    const language = params?.lan || params?.lang || (canonicalDirectory ? "EN" : "DE");
    if (!["DE", "EN"].includes(language)) throw new Error("Unsupported coverage language");
    url.searchParams.set("lang", language);
    url.searchParams.set("lan", language);
    const requestedProduct = params?.productName || DEFAULT_PRODUCT_NAME;
    if (!["THINKSCAN 689BT", "MUCAR 892BT", "MUCAR 892BT Pro", "THINKSCAN 689BT PRO", "MUCAR BT200 MAX", "THINKDIAG 2", "THINKSCAN 662", "MUCAR 682"].includes(requestedProduct)) {
      throw new Error("Unsupported coverage product");
    }
    url.searchParams.set("productName", requestedProduct);
    return url;
  }

  async function request(path, params, parentSignal) {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      if (parentSignal?.aborted) throw new DOMException("Cancelled", "AbortError");
      parentSignal?.addEventListener("abort", abort, { once: true });
      const timer = window.setTimeout(abort, 15000);
      try {
        const response = await fetch(buildUrl(path, params), {
          method: "GET", headers: { Accept: "application/json" },
          cache: "no-store", signal: controller.signal
        });
        if (!response.ok) throw new Error(`Coverage service returned ${response.status}`);
        const payload = await response.json();
        if (path === "getModelSelect" && [20, 21].includes(Number(payload.code)) && payload.data == null) {
          const error = new Error("Für diese Auswahl sind Modell- und Baujahrdaten in der Quelle noch nicht vollständig. Das bedeutet nicht, dass das Gerät inkompatibel ist. Kontaktieren Sie uns mit Ihrer FIN.");
          error.name = "SourceUnavailableError";
          throw error;
        }
        if (payload.success === false || (payload.appCode && payload.appCode !== "S0000")) {
          throw new Error("Coverage API rejected the request");
        }
        const valid = path === "selectModelList" ? Array.isArray(payload.result)
          : path === "getModelSelect" ? Array.isArray(payload.data)
          : Array.isArray(payload.result?.datas) && Number.isFinite(Number(payload.result.total));
        if (!valid) throw new Error("Coverage API returned an invalid response");
        return payload;
      } catch (error) {
        if (parentSignal?.aborted) throw new DOMException("Cancelled", "AbortError");
        if (error.name === "SourceUnavailableError") throw error;
        if (attempt === 1) {
          if (error.name === "AbortError") throw new DOMException("Timed out", "TimeoutError");
          throw error;
        }
      } finally {
        window.clearTimeout(timer);
        parentSignal?.removeEventListener("abort", abort);
      }
    }
  }

  function pushAnalytics(eventName, productLabel, payload = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      product: productLabel,
      ...payload
    });
  }

  async function requestMakeDirectory(params, signal) {
    let german, germanError;
    try {
      german = await request("selectModelList", { ...params, lang: "DE", lan: "DE" }, signal);
      if (german.result.length) return german;
    } catch (error) {
      if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
      germanError = error;
    }
    const english = await request("selectModelList", { ...params, lang: "EN", lan: "EN" }, signal);
    if (english.result.length) return english;
    if (germanError) throw germanError;
    return german;
  }

  function coverageDirectorySource(item, productName) {
    // The 689BT legacy directory omits both identifiers. Only its verified
    // 1:2 source may use that fallback; never silently assign it to another tool.
    const legacy689 = productName === "THINKSCAN 689BT";
    const pdtType = Number(item.pdtType ?? (legacy689 ? 1 : NaN));
    const pdtAliasId = Number(item.pdtAliasId ?? (legacy689 ? 2 : NaN));
    if (![1, 2].includes(pdtType) || !Number.isInteger(pdtAliasId) || pdtAliasId <= 0) {
      throw new Error("Die Datenquellenkennung dieses Geräts ist unvollständig. Bitte erneut versuchen oder den Support kontaktieren.");
    }
    return { id: String(item.swId), productName, pdtType, pdtAliasId };
  }


const localizedModelCache = new Map();
async function loadCoveragePage(
  baseParams,
  page,
  expectedRows,
  signal,
  confirmEmpty = false
) {
  let lastError;

  for (let attempt = 0; attempt <= PAGE_RETRY_COUNT; attempt += 1) {
    try {
      const payload = await request(
        "getModelPage",
        { ...baseParams, page },
        signal
      );
      const rows = Array.isArray(payload.result?.datas)
        ? payload.result.datas
        : [];
      const reportedTotal = Math.max(
        0,
        Number(payload.result?.total || 0)
      );
      const requiredRows =
        page === 1 && reportedTotal > 0
          ? Math.min(PAGE_SIZE, reportedTotal)
          : expectedRows;

      if (
        confirmEmpty &&
        rows.length === 0 &&
        attempt < PAGE_RETRY_COUNT
      ) {
        lastError = new Error(
          `Coverage page ${page} returned an empty result`
        );
        continue;
      }

      if (requiredRows > 0 && rows.length < requiredRows) {
        throw new Error(
          `Coverage page ${page} returned ${rows.length} of ${requiredRows} expected records`
        );
      }

      return { reportedTotal, rows };
    } catch (error) {
      if ((error?.name === "AbortError" || error?.name === "TimeoutError") || signal?.aborted) throw error;
      lastError = error;
      if (attempt === PAGE_RETRY_COUNT) throw lastError;
    }
  }

  throw lastError || new Error(`Coverage page ${page} could not be loaded`);
}

async function loadCoverageSource(vehicle, source, signal, language = "DE") {
  const baseParams = {
    lang: language, lan: language,
    productName: source.productName,
    modelName: source.id,
    model: vehicle.model,
    modelYear: vehicle.year,
    pdtType: source.pdtType,
    pdtAliasId: source.pdtAliasId,
    pageSize: PAGE_SIZE,
    size: PAGE_SIZE
  };

  const firstPage = await loadCoveragePage(baseParams, 1, 0, signal);
  const reportedTotal = firstPage.reportedTotal;
  const planningTotal = Math.max(reportedTotal, firstPage.rows.length);
  const pageCount = Math.max(1, Math.ceil(planningTotal / PAGE_SIZE));
  const rowsByPage = new Map([[1, firstPage.rows]]);

  for (
    let firstPageNumber = 2;
    firstPageNumber <= pageCount;
    firstPageNumber += PAGE_FETCH_BATCH_SIZE
  ) {
    const pages = Array.from(
      {
        length: Math.min(
          PAGE_FETCH_BATCH_SIZE,
          pageCount - firstPageNumber + 1
        )
      },
      (_, index) => firstPageNumber + index
    );
    const pageResults = await Promise.all(
      pages.map((page) => {
        const remainingRows = planningTotal - (page - 1) * PAGE_SIZE;
        const expectedRows = Math.min(PAGE_SIZE, remainingRows);
        return loadCoveragePage(
          baseParams,
          page,
          expectedRows,
          signal
        );
      })
    );

    pages.forEach((page, index) => {
      const pageRows = pageResults[index].rows;
      if (pageRows.length === PAGE_SIZE) {
        const signature = pageRows.map(completeCoverageRecordKey).join("|");
        for (const previousRows of rowsByPage.values()) {
          if (previousRows.length === PAGE_SIZE && previousRows.map(completeCoverageRecordKey).join("|") === signature) {
            throw new Error("Coverage pagination repeated a full page; completeness cannot be verified");
          }
        }
      }
      rowsByPage.set(page, pageRows);
    });
  }

  /*
   * `total` is useful for planning, but it is not trusted as the end of
   * the data. When the planned last page is full, probe subsequent pages
   * until the API returns a short page. This recovers records when total
   * is zero or underreported.
   */
  let lastPageNumber = pageCount;
  let lastPageRows = rowsByPage.get(lastPageNumber) || [];
  const seenFullPageSignatures = new Set();

  rowsByPage.forEach((pageRows) => {
    if (pageRows.length === PAGE_SIZE) {
      seenFullPageSignatures.add(
        pageRows
          .map(
            (row) =>
              row?.id ??
              completeCoverageRecordKey(row)
          )
          .join("|")
      );
    }
  });

  while (lastPageRows.length === PAGE_SIZE) {
    const nextPageNumber = lastPageNumber + 1;
    const nextPage = await loadCoveragePage(
      baseParams,
      nextPageNumber,
      0,
      signal,
      true
    );
    const nextSignature = nextPage.rows
      .map(
        (row) =>
          row?.id ??
          completeCoverageRecordKey(row)
      )
      .join("|");

    if (
      nextPage.rows.length === PAGE_SIZE &&
      seenFullPageSignatures.has(nextSignature)
    ) {
      throw new Error("Coverage pagination repeated a full page; completeness cannot be verified");
    }

    rowsByPage.set(nextPageNumber, nextPage.rows);
    if (nextPage.rows.length === PAGE_SIZE) {
      seenFullPageSignatures.add(nextSignature);
    }
    lastPageNumber = nextPageNumber;
    lastPageRows = nextPage.rows;
  }

  const rows = [...rowsByPage.entries()]
    .sort(([pageA], [pageB]) => pageA - pageB)
    .flatMap(([, pageRows]) => pageRows);
  const resolvedTotal = Math.max(reportedTotal, rows.length);

  if (reportedTotal > 0 && rows.length < reportedTotal) {
    throw new Error(
      `Coverage pagination returned ${rows.length} of ${reportedTotal} records`
    );
  }

  // DE selectors translate model labels, while the server may filter on
  // untranslated names. Verify against every DE row for this brand/year.
  // This never mixes products, years, or language datasets.
  if (!rows.length && vehicle.model && language === "DE") {
    const cacheKey = JSON.stringify([source.productName, source.id, source.pdtType, source.pdtAliasId, vehicle.year, language]);
    let broad = localizedModelCache.get(cacheKey);
    if (!broad) {
      broad = await loadCoverageSource({ ...vehicle, model: null }, source, signal, language);
      if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
      localizedModelCache.set(cacheKey, broad);
      if (localizedModelCache.size > 8) localizedModelCache.delete(localizedModelCache.keys().next().value);
    }
    const normalize = (value) => String(value ?? "").normalize("NFC").replace(/\s+/g, " ").trim();
    const matching = broad.rows.filter((row) =>
      normalize(row.model) === normalize(vehicle.model) &&
      normalize(row.modelYear) === normalize(vehicle.year) &&
      String(row.modelName).toUpperCase() === String(source.id).toUpperCase()
    );
    return { ...broad, total: matching.length, rows: matching,
      pagesFetched: 1 + broad.pagesFetched, localizedModelVerified: true };
  }

  // Software/source id, exact model key and year must agree. Null keys
  // cannot prove compatibility with a selected vehicle.
  const normalizeKey = (value) => String(value ?? "").normalize("NFC").replace(/\s+/g, " ").trim();
  if (rows.some((row) =>
    String(row.modelName ?? "").toUpperCase() !== String(source.id).toUpperCase() ||
    (vehicle.model && normalizeKey(row.model) !== normalizeKey(vehicle.model)) ||
    (vehicle.year && normalizeKey(row.modelYear) !== normalizeKey(vehicle.year))
  )) throw new Error("Coverage source returned records outside the selected model/year");

  return {
    language,
    total: resolvedTotal,
    rows,
    truncated: false,
    pagesFetched: rowsByPage.size,
    productName: source.productName,
    pdtType: source.pdtType,
    pdtAliasId: source.pdtAliasId
  };
}

async function loadCoverageWithLanguageFallback(vehicle, source, signal) {
  const readLanguage = async (language) => {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
    signal?.addEventListener("abort", abort, { once: true });
    try {
      return await loadCoverageSource(vehicle, source, controller.signal, language);
    } finally {
      // Discard/cancel every incomplete DE page before starting EN.
      controller.abort();
      signal?.removeEventListener("abort", abort);
    }
  };
  let german, germanError;
  try {
    german = await readLanguage("DE");
    if (german.rows.length) return { ...german, fallbackReason: null, checkedLanguages: ["DE"] };
  } catch (error) {
    if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
    germanError = error;
  }
  if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
  // A successful empty DE result may mean missing translation data, not
  // unsupported hardware. EN must also complete before concluding no data.
  const english = await readLanguage("EN");
  if (english.rows.length) return {
    ...english,
    fallbackReason: germanError ? "de-unavailable-or-incomplete" : "de-no-matching-data",
    checkedLanguages: ["DE", "EN"]
  };
  if (germanError) {
    const error = new Error("Die deutsche Datenquelle ist nicht verfügbar und die englische Quelle hat keine prüfbaren Daten geliefert. Bitte erneut versuchen oder den Support kontaktieren.");
    error.name = "SourceUnavailableError";
    throw error;
  }
  return { ...german, checkedLanguages: ["DE", "EN"], fallbackReason: null };
}


  const catalogueCache = new Map();
  const selectorCache = new Map();
  const completeCache = new Map();
  const sourceKey = source => JSON.stringify([source.productName,source.id,source.pdtType,source.pdtAliasId]);
  const cache = async (map,key,read,signal) => {
    if (signal?.aborted) throw new DOMException("Cancelled","AbortError");
    // Cache complete values, not in-flight work belonging to another request.
    if (map.has(key)) return map.get(key);
    const value = await read();
    if (signal?.aborted) throw new DOMException("Cancelled","AbortError");
    map.set(key,value);
    if (map.size>96) map.delete(map.keys().next().value);
    return value;
  };
  const usable = payload => payload.result.filter(item => item?.swId && !/^RESET/i.test(item.swId) &&
    (item.vehicleType==null || Number(item.vehicleType)===2));
  const makeList = (product,signal) => cache(catalogueCache,product.apiName,()=>requestMakeDirectory({
    productName:product.apiName,pdtType:1,channel:3,vehicleType:2
  },signal),signal);
  const sourceFor = (product,item) => coverageDirectorySource(item,product.apiName);
  const mappings = async (product,makeId,signal) => {
    const map=new Map();
    usable(await makeList(product,signal)).filter(item=>String(item.swId).toUpperCase()===String(makeId).toUpperCase())
      .forEach(item=>{const source=sourceFor(product,item);map.set(sourceKey(source),source);});
    return [...map.values()];
  };
  const selector = (source,type,model,signal) => cache(selectorCache,JSON.stringify([sourceKey(source),type,model||""]),
    ()=>request("getModelSelect",{swId:source.id,productName:source.productName,
      pdtType:source.pdtType,pdtAliasId:source.pdtAliasId,type,...(model?{model}:{})},signal),signal);
  const coverage = async (product,vehicle,signal) => {
    const sources=await mappings(product,vehicle.makeId,signal);
    const sourceResults=await Promise.all(sources.map(source=>cache(completeCache,
      JSON.stringify([sourceKey(source),vehicle.model,vehicle.year]),
      ()=>loadCoverageWithLanguageFallback(vehicle,source,signal),signal)));
    return {sourceResults,rows:sourceResults.flatMap(result=>result.rows),
      total:sourceResults.reduce((sum,result)=>sum+result.total,0),
      pagesFetched:sourceResults.reduce((sum,result)=>sum+result.pagesFetched,0),
      languages:[...new Set(sourceResults.map(result=>result.language))],
      englishFallback:sourceResults.some(result=>result.language==="EN"),sources};
  };
  const normalize = value => String(value||"").normalize("NFD").replace(/[\u0300-\u036f]/g,"")
    .toLowerCase().replace(/[‐‑‒–—−/\\_’']/g," ").replace(/[^a-z0-9]+/g," ").trim();
  const category = row => {
    // Classify the canonical capability, not arbitrary words in task descriptions.
    const label=normalize(row.function || row.subFunction);
    if (/\b(?:aktiver? tests?|aktive tests|aktorentests?|stellgliedtests?|betatigungstests?|ansteuerungstests?|bidirektionale (?:tests?|steuerung))\b/.test(label)) return "active";
    if (/\b(?:codierung|codieren|programm|programmierung|programmieren|konfiguration)\b/.test(label)) return "coding";
    if (/\b(?:besondere|spezie?lle|sonder|wartungs)(?: funktion(?:en)?|funktion(?:en)?)\b|\b(?:ruckstellung|zurucksetzen|anlernen)\b/.test(label)) return "service";
    if (/\b(?:fehlercodes?|fehlerspeicher|storungscodes?|live daten|echtzeitdaten|datenstrom|rahmen einfrieren|einfrierrahmen|standbild)\b/.test(label)) return "warning";
    if (/\b(?:active|actuation|actuator|activation|output|component) tests?\b|\bbi directional (?:tests?|control)\b|\btests? (?:actifs?|d actionnement|d actionneurs?|des actionneurs?|actionneurs?)\b|\bcommande bidirectionnelle\b/.test(label)) return "active";
    if (/\b(?:coding|program|programme|programming|configuration|codage|programmation)\b/.test(label)) return "coding";
    if (/\b(?:special|service|maintenance) functions?\b|\b(?:service )?resets?\b|\brelearns?\b|\bfonctions? (?:speciales?|d entretien|de maintenance)\b|\breinitialisation\b/.test(label)) return "service";
    if (/\b(?:trouble|fault|diagnostic) codes?\b|\b(?:read|clear|erase) (?:dtcs?|codes?)\b|\bdtcs?\b|\bcodes? (?:de probleme|de defauts?|defauts?|d erreur)\b/.test(label)) return "warning";
    if (/\blive data|data stream|freeze frame|donnees? (?:en direct|en temps reel|figees?)|flux de donnees|plan fige|cadre de gel\b/.test(label)) return "warning";
    return "other";
  };
  const isForNeed = (row,need) => {
    if (need==="systems") return Boolean(row && (row.system || row.function || row.subFunction));
    if (need==="tpms") return /\btpms\b|tire\s*pressure|tyre\s*pressure|air\s+pressure\s+monitor|pression\s+des?\s+pneus|pression\s+(?:de|des)\s+pneumatiques|\brdks\b|reifendruck|luftdruck[uü]berwachung/i.test(
      [row.system,row.subSystem,row.function,row.subFunction].filter(Boolean).join(" "));
    return category(row)===need;
  };
  const rowAllowed = (row,product) => !(product.hiddenForNeeds||[]).includes(category(row));
  window.TcDeRecommendationData=Object.freeze({makeList,usable,sourceFor,mappings,selector,coverage,
    request,buildUrl,loadCoverageSource,loadCoverageWithLanguageFallback,completeCoverageRecordKey,
    cleanSystemLabel,coverageRecordDetail,category,isForNeed,rowAllowed,
    clear:()=>{catalogueCache.clear();selectorCache.clear();completeCache.clear();localizedModelCache.clear();}});
})();
